import { Link } from "react-router-dom";
import { ArrowRight, Calculator, Receipt, Users, Car, Briefcase, CheckCircle2 } from "lucide-react";
import { useSEO, buildFAQSchema } from "../hooks/useSEO";
import { AdSlot } from "../components/AdSlot";

const CALCULATORS = [
  { slug: "no-tax-on-tips", title: "No Tax on Tips", blurb: "Deduct up to $25,000 of qualified tips under OBBBA.", tag: "NEW", icon: Receipt },
  { slug: "no-tax-on-overtime", title: "No Tax on Overtime", blurb: "Up to $12,500 single / $25,000 joint deduction for overtime premium pay.", tag: "NEW", icon: Briefcase },
  { slug: "senior-deduction-2026", title: "Senior $6,000 Deduction", blurb: "Extra deduction for taxpayers 65+, phase-outs apply.", tag: "NEW", icon: Users },
  { slug: "salt-deduction-2026", title: "$40,000 SALT Cap", blurb: "Raised from $10k to $40k, phase-out above $500k MAGI.", tag: "UPDATED", icon: Calculator },
  { slug: "tax-bracket-2026", title: "2026 Tax Bracket", blurb: "Real 2026 IRS brackets with OBBBA's extra inflation bump.", tag: "", icon: Calculator },
  { slug: "child-tax-credit-2026", title: "Child Tax Credit", blurb: "Permanent $2,200 per child under OBBBA, inflation-indexed.", tag: "", icon: Users },
  { slug: "standard-deduction-2026", title: "Standard Deduction", blurb: "$16,100 (single) / $32,200 (MFJ) for 2026 — permanent.", tag: "", icon: Calculator },
  { slug: "qbi-deduction-2026", title: "QBI Deduction (Permanent)", blurb: "20% pass-through deduction now permanent under OBBBA.", tag: "", icon: Briefcase },
  { slug: "capital-gains-2026", title: "Capital Gains", blurb: "2026 LTCG brackets for 0%, 15%, and 20% rates.", tag: "", icon: Calculator },
  { slug: "auto-loan-interest-2026", title: "Auto Loan Interest", blurb: "New: deduct up to $10,000 of new-vehicle auto loan interest.", tag: "NEW", icon: Car },
  { slug: "estate-tax-2026", title: "Estate Tax Exemption", blurb: "$15M single / $30M joint — raised and made permanent.", tag: "", icon: Calculator },
];

const FAQ = [
  {
    q: "What is OBBBA and when did it take effect?",
    a: "The One Big Beautiful Bill Act (OBBBA), Public Law 119-21, was signed into law on July 4, 2025. Most provisions apply starting tax year 2025, with further changes and inflation adjustments taking effect in 2026. It permanently extended most TCJA provisions and created several new deductions (tips, overtime, senior, auto loan interest).",
  },
  {
    q: "Are my taxes going up or down in 2026?",
    a: "For most households, slightly down. The standard deduction rises (inflation plus OBBBA bonus), tax brackets widen, the Child Tax Credit is permanently at $2,200, the SALT cap is $40,000 (vs the old $10,000), and new deductions are available for tipped and overtime workers and seniors. High earners above $500k MAGI face SALT cap phase-outs.",
  },
  {
    q: "What's new in 2026 vs 2025 under OBBBA?",
    a: "The standard deduction, tax brackets, and capital gains thresholds are inflation-adjusted upward. The SALT cap rises from $40,000 to $40,400. The estate tax exemption jumps from $13.99M to $15M. The dependent-care credit maximum rises to 50%. The 1099 reporting threshold rises to $2,000.",
  },
  {
    q: "Who qualifies for the No Tax on Tips deduction?",
    a: "Workers in occupations on the IRS list of customarily-and-regularly-tipped occupations (hospitality, personal services, etc.). The deduction is up to $25,000 of tip income, phased out above $150,000 MAGI (single) or $300,000 (married joint). Tips are still subject to payroll (FICA) taxes.",
  },
  {
    q: "Is TaxShift 2026 free?",
    a: "Yes. All calculators and articles are 100% free. We fund operations through Google AdSense advertising. No sign-up, no paywall.",
  },
  {
    q: "Is this tax advice?",
    a: "No. TaxShift 2026 provides educational information and illustrative calculators based on publicly known law. Always consult a qualified CPA, EA, or tax attorney before making financial decisions.",
  },
];

const HomePage = () => {
  useSEO({
    title: "OBBBA Tax Navigator — Free 2026 Federal Tax Calculators",
    description:
      "Free 2026 tax calculators for OBBBA's new deductions: No Tax on Tips, No Tax on Overtime, $6,000 Senior Deduction, $40k SALT cap, child tax credit, brackets, QBI, estate tax.",
    keywords:
      "obbba tax calculator, 2026 tax calculator, no tax on tips calculator, no tax on overtime calculator, senior deduction calculator, 40k salt cap, 2026 tax brackets",
    path: "/",
    jsonLd: buildFAQSchema(FAQ),
  });

  return (
    <>
      <section className="border-b-2 border-foreground" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-24 pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3 mb-6">
                <span className="font-mono uppercase text-[10px] tracking-[0.3em] bg-primary text-primary-foreground px-2 py-1">
                  OBBBA · Public Law 119-21
                </span>
                <span className="font-mono uppercase text-[10px] tracking-[0.3em] text-muted-foreground">
                  Vol. 2 · Edition 2026
                </span>
              </div>
              <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[0.95] mb-6" data-testid="hero-title">
                Your 2026 Tax Law,
                <br />
                <span className="italic text-primary">finally calculated right.</span>
              </h1>
              <p className="font-body text-lg md:text-xl text-foreground/80 leading-relaxed max-w-2xl mb-8">
                The One Big Beautiful Bill Act rewrote your 2026 taxes. New deductions for tips,
                overtime, and seniors. A $40k SALT cap. Permanent QBI. A $2,200 Child Tax Credit.{" "}
                <span className="font-semibold">Eleven free calculators, real IRS numbers.</span>
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/calculator/no-tax-on-tips"
                  data-testid="hero-cta-primary"
                  className="bg-primary text-primary-foreground font-mono uppercase tracking-widest text-xs px-8 py-5 inline-flex items-center gap-3 hover:-translate-y-1 transition-all brutalist-shadow-sm hover:brutalist-shadow"
                >
                  Try the Tips Calculator
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/calculators"
                  data-testid="hero-cta-secondary"
                  className="font-mono uppercase tracking-widest text-xs px-8 py-5 border-2 border-foreground hover:bg-foreground hover:text-background transition-colors inline-flex items-center justify-center gap-2"
                >
                  All 11 Calculators
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="bg-card border-2 border-foreground brutalist-shadow p-6 md:p-8" data-testid="hero-summary-card">
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-foreground/20">
                  <span className="font-mono uppercase text-[10px] tracking-[0.25em] text-muted-foreground">
                    What OBBBA Delivered
                  </span>
                  <CheckCircle2 className="w-5 h-5 text-primary" />
                </div>
                <div className="space-y-5">
                  <div className="flex items-baseline justify-between pb-4 border-b border-foreground/10">
                    <span className="font-body text-sm">Tips deduction</span>
                    <span className="font-mono text-xl tabular-nums text-primary font-semibold">up to $25k</span>
                  </div>
                  <div className="flex items-baseline justify-between pb-4 border-b border-foreground/10">
                    <span className="font-body text-sm">Overtime deduction</span>
                    <span className="font-mono text-xl tabular-nums text-primary font-semibold">$12.5k–$25k</span>
                  </div>
                  <div className="flex items-baseline justify-between pb-4 border-b border-foreground/10">
                    <span className="font-body text-sm">Senior (65+) extra deduction</span>
                    <span className="font-mono text-xl tabular-nums text-primary font-semibold">$6,000</span>
                  </div>
                  <div className="flex items-baseline justify-between pb-4 border-b border-foreground/10">
                    <span className="font-body text-sm">SALT cap</span>
                    <span className="font-mono text-xl tabular-nums text-primary font-semibold">$40,000</span>
                  </div>
                  <div className="flex items-baseline justify-between pb-4 border-b border-foreground/10">
                    <span className="font-body text-sm">Child Tax Credit</span>
                    <span className="font-mono text-xl tabular-nums text-primary font-semibold">$2,200 permanent</span>
                  </div>
                  <div className="flex items-baseline justify-between">
                    <span className="font-body text-sm">Estate exemption</span>
                    <span className="font-mono text-xl tabular-nums text-primary font-semibold">$15M permanent</span>
                  </div>
                </div>
                <div className="mt-6 pt-5 border-t border-foreground/20">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    Real IRS figures · Public Law 119-21
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <AdSlot size="leaderboard" testid="home-leaderboard-ad" />

      <section className="py-20 md:py-28 border-b-2 border-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-6">
            <div>
              <span className="font-mono uppercase text-[10px] tracking-[0.3em] text-muted-foreground mb-3 block">
                § 01 · Calculator Suite
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight max-w-3xl">
                Eleven calculators. Every major OBBBA <span className="italic">provision</span>.
              </h2>
            </div>
            <Link
              to="/calculators"
              className="font-mono uppercase tracking-widest text-xs flex items-center gap-2 hover:text-primary transition-colors"
              data-testid="all-calculators-link"
            >
              See all eleven <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-foreground/20">
            {CALCULATORS.map((c, idx) => {
              const Icon = c.icon;
              return (
                <Link
                  key={c.slug}
                  to={`/calculator/${c.slug}`}
                  className="group relative border-r border-b border-foreground/20 p-8 bg-card hover:bg-primary hover:text-primary-foreground transition-all duration-200"
                  data-testid={`calc-card-${c.slug}`}
                >
                  <div className="flex items-start justify-between mb-6">
                    <Icon className="w-6 h-6" />
                    {c.tag && (
                      <span
                        className={`font-mono text-[9px] uppercase tracking-widest px-2 py-1 ${
                          c.tag === "NEW" ? "bg-primary text-primary-foreground group-hover:bg-primary-foreground group-hover:text-primary" : "bg-foreground text-background group-hover:bg-primary-foreground group-hover:text-primary"
                        }`}
                      >
                        {c.tag}
                      </span>
                    )}
                  </div>
                  <h3 className="font-heading text-2xl font-semibold mb-3 leading-tight">{c.title}</h3>
                  <p className="font-body text-sm text-foreground/80 group-hover:text-primary-foreground/90 leading-relaxed mb-6">
                    {c.blurb}
                  </p>
                  <div className="font-mono text-xs uppercase tracking-widest flex items-center gap-2">
                    Calculate <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <AdSlot size="leaderboard" testid="home-mid-ad" />

      <section className="py-20 md:py-28 border-b-2 border-foreground">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="font-mono uppercase text-[10px] tracking-[0.3em] text-muted-foreground mb-3 block">
            § 02 · Answers
          </span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mb-12">
            Frequently asked.
          </h2>
          <div className="space-y-0 border-t border-foreground/20">
            {FAQ.map((f, i) => (
              <details
                key={i}
                className="group border-b border-foreground/20 py-6 cursor-pointer"
                data-testid={`faq-${i}`}
              >
                <summary className="flex items-center justify-between gap-4 list-none">
                  <h3 className="font-heading text-xl font-semibold group-open:text-primary transition-colors">
                    {f.q}
                  </h3>
                  <span className="font-mono text-2xl group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="font-body text-base text-foreground/80 mt-4 leading-relaxed">
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-foreground text-background py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="font-heading text-3xl md:text-4xl font-bold mb-4">
            Real OBBBA law. Real numbers. Always free.
          </h3>
          <p className="font-body text-base text-background/80 mb-8 max-w-2xl mx-auto">
            Updated annually as the IRS publishes new inflation adjustments.
          </p>
          <Link
            to="/calculators"
            data-testid="footer-cta-calculate"
            className="inline-flex items-center gap-3 bg-primary text-primary-foreground font-mono uppercase tracking-widest text-xs px-8 py-5 hover:brightness-110 transition"
          >
            Open All Calculators <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </>
  );
};

export default HomePage;
